import { DataTypeProvider } from '@devexpress/dx-react-grid';
import Button from '@mui/material/Button';
import Box from '@mui/material/Box';
import Tooltip from '@mui/material/Tooltip';
import { useContext, useMemo } from 'react';
import { FiltersContext } from '../../../pages/NRReportTabs/NRReportFilterContextWrapper';
import NRSBOMDrawer from '../../../pages/NRSBOMTable/NRSBOMDrawer/NRSBOMDrawer';
import { useTheme } from '@mui/material/styles';
import { CONSTANTS } from '../../../constants/constants';
import Typography from '@mui/material/Typography';
import NRAITag from '../../../NRIcons/NRAITag';
import NRTooltipContent from '../../NRTooltipContent/NRTooltipContent';
import { getAIProviderIcon } from '../../NRAiProvider/aiProviders.utils';

const { LOADING } = CONSTANTS.TABLE.MISCONFIGURATION_TABLE.MISCONFIGURATION_DETAIL_DRAWER;

export const confidenceAttr = 'fidelity';
const SbomNameFormatter = ({ value, column, row }) => {
  const theme = useTheme();
  const filtersContext = useContext(FiltersContext);
  const addAITag = row.isAi;
  const aiProviders = Array.isArray(row.aiProviders) ? row.aiProviders : row.aiProviders ? [row.aiProviders] : [];

  const aiProvidersTooltipContent = useMemo(
    () => (
      <Box>
        {aiProviders.length ? (
          aiProviders.map(provider => {
            const ProviderIcon = getAIProviderIcon({ provider, useDarkIcon: true, theme });
            return (
              <Box key={provider} sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 0.5 }}>
                <ProviderIcon width={20} height={20} />
                <Typography component="p" variant="caption">
                  {provider}
                </Typography>
              </Box>
            );
          })
        ) : (
          <Typography component="p" variant="caption">
            -
          </Typography>
        )}
      </Box>
    ),
    [aiProviders, theme]
  );

  // Check if FiltersContext is properly provided (not just default values)
  // Default context has openDrawer: null, but real context has boolean
  // Also check that drawerContent is an array (not null) to confirm it's the real context
  const isDrawerAvailable =
    filtersContext &&
    filtersContext.setOpenDrawer &&
    typeof filtersContext.setOpenDrawer === 'function' &&
    filtersContext.openDrawer !== null && // Default context has null
    Array.isArray(filtersContext.drawerContent); // Real context has array

  const handleOpenDrawer = () => {
    if (!isDrawerAvailable) {
      // Drawer not available, do nothing
      return;
    }

    const { setOpenDrawer, setDrawerContent, drawerContent, setDrawerTitle, drawerTitle, setHasBack } = filtersContext;
    !!drawerContent.length && setHasBack(true);
    setDrawerTitle([...drawerTitle, LOADING]);
    setDrawerContent([...drawerContent, <NRSBOMDrawer row={row} />]);
    setOpenDrawer(true);
  };

  return (
    <Button
      aria-label={isDrawerAvailable ? `Open details drawer for ${value}` : `Component name: ${value}`}
      onClick={handleOpenDrawer}
      disabled={!isDrawerAvailable}
      sx={{
        inset: 0,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'flex-start',
        cursor: isDrawerAvailable ? 'pointer' : 'default',
        px: 2,
        py: 1.5,
        width: '100%',
        height: '100%',
        minWidth: 'auto',
        textTransform: 'none',
        textAlign: 'left',
        backgroundColor: 'transparent',
        boxShadow: 'none',
        '&:hover': {
          backgroundColor: 'transparent',
          boxShadow: 'none'
        },
        '&:focus-visible': {
          outline: '2px solid',
          outlineOffset: '2px',
          outlineColor: theme.palette.primary.main,
          backgroundColor: 'transparent'
        },
        '&:active': {
          backgroundColor: 'transparent',
          boxShadow: 'none'
        },
        '&:disabled': {
          cursor: 'default'
        }
      }}
      disableRipple
    >
      <Typography
        variant="body2"
        color={theme.palette.text.secondary}
        sx={{
          textDecoration: isDrawerAvailable ? 'underline' : 'none',
          ...(addAITag && { marginRight: theme.spacing(2) })
        }}
      >
        {value || '-'}
      </Typography>
      {addAITag && (
        <Tooltip title={<NRTooltipContent title="AI Component Providers" body={aiProvidersTooltipContent} />} placement="top">
          <Box sx={{ ml: 'auto', flexShrink: 0, display: 'inline-flex' }}>
            <NRAITag height="20" width="20" color={theme.palette.riseAiPurple} />
          </Box>
        </Tooltip>
      )}
    </Button>
  );
};

export const NRSBOMNameProvider = props => <DataTypeProvider formatterComponent={SbomNameFormatter} {...props} />;
